
import React, {useContext, useEffect, useRef, useState} from 'react'
import { useTranslation } from 'react-i18next'
import {
	Button,
	Card,
	CardBody,
	Col, Modal, ModalBody, ModalHeader,
	Row
} from 'reactstrap'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import utc from 'dayjs/plugin/utc'
import timeZone from 'dayjs/plugin/timezone'
dayjs.extend(localizedFormat)
dayjs.extend(utc)
dayjs.extend(timeZone)

import VehiclePicSliderRedux from '../Slider/VehiclePicSliderRedux'
import Enumeration from '../../enumeration'
import {subscribe, trigger, unsubscribe} from '../../events/customEvents'
import { AuctionsContext } from '../../contexts/AuctionsProvider'
import { SessionContext } from '../../contexts/SessionProvider'
import SiteService from '../../services/SiteService'

const AuctionListGroupItem = ({
	idx,
	auctionListItem,
	showVehiclePicCollection,
	removeFromWatchListClicked,
	viewDetailsClicked,
	placeBuyNowClicked,
	placeBidClicked,
	addToWatchlistClicked,
	componentViewSettings,
	visible,
	observe,
	unobserve,
	asDescribedImage,
	conditionReportImage,
	firstRunImage
}) =>
{
	const { t } = useTranslation()
	const ref = useRef(null)
	const auctionsContext = useContext(AuctionsContext)
	const sessionContext = useContext(SessionContext)
	const [selectedPicFilePath, setSelectedPicFilePath] = useState('')

	
	const activeCounter = auctionListItem.auctionTier && auctionListItem.auctionTier.yourBid && auctionListItem.auctionTier.yourBid.counterBid
	let activeCounterTimeDisplay = null
	if (activeCounter && auctionListItem.auctionTier.yourBid.counterBid.counterUtcExpiryTime)
	{
		const counterSecondsRemaining = dayjs(auctionListItem.auctionTier.yourBid.counterBid.counterUtcExpiryTime).diff(dayjs(), 'second')
		if (counterSecondsRemaining > 0)
		{
			activeCounterTimeDisplay = SiteService.setExpiryTimeDisplay(counterSecondsRemaining)
		}
	}

	const onMount = () =>
	{
		observe(ref)
		return () => unobserve(ref)
	}

	useEffect(onMount, [])

	useEffect(() =>
	{
		subscribe('auctionListImpression', handleAuctionListImpression)
	}, [])

    useEffect(() =>
    {
        return () =>
        {
            unsubscribe('auctionListImpression', handleAuctionListImpression)
        }
    }, [])

	const handleAuctionListImpression = (event) =>
	{
		event.stopImmediatePropagation()
		event.preventDefault()
		try
		{
			const auctionTierMemberId = auctionsContext.memberPagingResult.pageCollection[parseInt(event.detail.auctionListIndex)].auctionTier.auctionTierMemberId
			trigger('auctionListGroupItemIntersection', { auctionTierMemberId: auctionTierMemberId })
		}
		catch {}
	}

	const handleChangePrimaryDisplayImage = (imageUrl) =>
	{
		auctionListItem.primaryDisplayImageUrl = imageUrl
	}

	const getListGroupItemClasses = () =>
	{
		return `px-0 auction-list-item-${ idx }`
	}
	
	const handleSmallInspectionPicClicked = (filePath) =>
	{
		setSelectedPicFilePath(filePath)
	}
	
	const handleLargeInspectionPicClicked = () =>
	{
		setSelectedPicFilePath('')
	}
	
	return (
		<div className={ getListGroupItemClasses() } ref={ref} >
			<Card>
				<CardBody>
					<Row className="row-example">
						<Col md="6" xs="12">
							<Row>
								<Col md="4">
									<img
										className="inspection-pic-sm"
										src={ auctionListItem.primaryDisplayImageUrl }
										alt="..."
										width="170"
										height="127"
										onClick={ () => { handleSmallInspectionPicClicked(auctionListItem.primaryDisplayImageUrl) } }
										style={{ cursor: 'pointer' }}
									/>
								</Col>
								<Col md="8" className="text-primary">
									<div style={{ marginLeft: '5px' }}><label style={{ fontSize: '12px' }}>{ auctionListItem.year + ' ' + auctionListItem.make + ' ' + auctionListItem.model + ' ' + auctionListItem.trim }</label></div>
									<div style={{ marginLeft: '5px' }}><label style={{ fontSize: '12px' }}>{  'Int: ' + auctionListItem.interiorColor + ' | ' + SiteService.numberDisplay(auctionListItem.cylinders, 0) + ' Cyl | ' + auctionListItem.displacement + ' | ' + auctionListItem.drivetrain + ' ' + auctionListItem.serialNumber }</label></div>
									<div style={{ marginLeft: '5px' }}>
										<img alt="..." style={{ display: 'inline-block', marginRight: '5px' }} height="24px" width="24px" src={ asDescribedImage } />
										{
											auctionListItem.hasVehicleInspection &&
											<img alt="..." style={{ display: 'inline-block',marginRight: '5px'}} height="24px" width="24px" src={ conditionReportImage } />
										}
										{ /* BRD UAT #6: warning triangle when a Carfax report is on file for the vehicle */ }
										{ auctionListItem.hasCarfaxReport && (
											<span
												className="badge bg-warning"
												style={{ fontSize: '10px', marginRight: '5px', verticalAlign: 'middle', cursor: 'help' }}
												title="Carfax report available — see vehicle detail"
											>
												&#9888; Carfax
											</span>
										) }
										<div style={{
											display: 'inline-block',
											marginRight: '5px',
											height: '24px',
											width: '24px',
											backgroundColor: '#3C9F40',
											color: '#FFFFFF',
											textAlign: 'center'
										}}>
											{ auctionListItem.totalVinScanItems }
										</div>
									</div>
								</Col>
							</Row>
							{
								showVehiclePicCollection &&
								<Row style={{ marginTop: '10px' }}>
									<Col md="12">
										<VehiclePicSliderRedux
											vehiclePics={ auctionListItem.vehicleInspectionPictures }
											changePrimaryDisplayImage={ handleChangePrimaryDisplayImage }
										/>
									</Col>
								</Row>
							}
						</Col>
						<Col md="2" xs="2">
							<Card style={{ borderStyle: 'solid', borderWidth: '1px', backgroundColor: '#f6f9fc', height: '99%' }}>
								<CardBody style={{ padding: '12px' }}>
									<Col md="12" className="text-primary" >
										<div><label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.Odometer') + ':' }</label><span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>{ SiteService.numberDisplay(auctionListItem.mileage, 0) + ' km' }</span></div>
										<div><label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.Damages') + ':' }</label><span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>{ SiteService.priceDisplay(auctionListItem.totalDamages) }</span></div>
                                        <div><label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.Province') + ':' }</label><span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>{ auctionListItem.locationProvince }</span></div>
                                        <div><label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.Proximity') + ':' }</label><span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>{ SiteService.numberDisplay(auctionListItem.proximity, 2) + ' km' }</span></div>
									</Col>
								</CardBody>
							</Card>
						</Col>
						<Col md="2" xs="2">
							<Card style={{ borderStyle: 'solid', borderWidth: '1px', backgroundColor: '#f6f9fc', height: '99%' }}>
								<CardBody style={{ padding: '12px' }}>
									<Col md="12" className="text-primary" >
                                        <div><label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.Time_Left') + ':' }</label><span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>{ activeCounterTimeDisplay ? (activeCounterTimeDisplay.h + 'h ' + activeCounterTimeDisplay.m + 'm ' + activeCounterTimeDisplay.s + 's') : (auctionListItem.timeDisplay.h + 'h ' + auctionListItem.timeDisplay.m + 'm ' + auctionListItem.timeDisplay.s + 's') }</span></div>
										{
											/* MARKET PRICE */
											(auctionListItem.auctionTier.auctionTierTypeId !== Enumeration.AuctionTierTypes.BiddingTier) &&
											<div>
												<label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.Market') + ':' }</label>
												<span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>
													{ SiteService.priceDisplay(auctionListItem.marketPrice) }
												</span>
											</div>
										}
										{
											/* START PRICE */
                                            (auctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier && auctionListItem.startPrice > auctionListItem.highestBidAmount) &&
                                            <div>
												<label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.Start') + ':' }</label>
												<span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>
													{ SiteService.priceDisplay(auctionListItem.startPrice) }
												</span>
											</div>
                                        }
                                        {
											/* HIGHEST BID */
                                            (auctionListItem.highestBidAmount >= auctionListItem.startPrice) &&
                                            <div>
												<label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.High_Bid') + ':' }</label>
												<span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>
													{ (auctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier) ? SiteService.priceDisplay(auctionListItem.highestBidAmount) : 'N/A' }
												</span>
												{ /* BRD: Reserve Met indicator when reserve has been met on the bidding tier */ }
												{ auctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier && auctionListItem.isReserveMet && (
													<span
														className="badge bg-success"
														style={{ fontSize: '10px', marginLeft: '4px', verticalAlign: 'middle' }}
														title="High bid meets the reserve price"
													>
														Reserve Met
													</span>
												) }
											</div>
                                        }
                                        {
											/* BUY NOW PRICE */
                                            (auctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier && SiteService.isElementViewable('DealerAuctionsListing','BuyNow_Price', componentViewSettings) && (auctionListItem.buyNowPrice !== undefined && auctionListItem.buyNowPrice > 0)) &&
                                            <div><label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.Buy_Now') + ':' }</label><span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>{ SiteService.priceDisplay(auctionListItem.buyNowPrice) }</span></div>
                                        }
                                        <div>
                                            
                                            {
												auctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier && auctionListItem.auctionTier.yourBid &&
												<>
													<label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.Your_Bid') + ':' }</label>
													<span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>
														{ SiteService.priceDisplay(auctionListItem.auctionTier.yourBid.bidAmount) }
													</span>
												</>
                                            }
                                            {
												auctionListItem.auctionTier.auctionTierTypeId !== Enumeration.AuctionTierTypes.BiddingTier || !auctionListItem.auctionTier.yourBid &&
												<>
													<label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.Your_Bid') + ':' }</label>
													<span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>N/A</span>
												</>
                                            }
                                        </div>
                                        {
											auctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier &&
											<div>
												<label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>Min Bid:</label>
												<span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>
													{
														SiteService.priceDisplay(
															(auctionListItem.highestBidAmount && auctionListItem.highestBidAmount > 0)
																? (auctionListItem.highestBidAmount + 100)
																: auctionListItem.startPrice
														)
													}
												</span>
											</div>
                                        }
                                        {
                                            auctionListItem.auctionTier && auctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier && auctionListItem.auctionTier.yourBid && auctionListItem.auctionTier.yourBid.counterBid &&
                                            <>
                                                <div><hr style={{ margin: '0px' }} /></div>
                                                <div>
                                                    <label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.Counter_Bid') + ':' }</label>
													{ (auctionListItem.auctionTier.yourBid && auctionListItem.auctionTier.yourBid.counterBid) &&
														<span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>
															{ SiteService.priceDisplay(auctionListItem.auctionTier.yourBid.counterBid.counterBidAmount) }
														</span>
													}
                                                </div>
                                                <div>
                                                    <label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Listing.Counter_Expires') + ':' }</label>
													{ (auctionListItem.auctionTier.yourBid && auctionListItem.auctionTier.yourBid.counterBid) &&
														<span style={{ fontSize: '12px', padding: '5px', marginBottom: '10px', marginTop: '0' }}>
															{ SiteService.dateTimeDisplayEastern(auctionListItem.auctionTier.yourBid.counterBid.counterUtcExpiryTime) }
														</span>
													}
                                                </div>
                                            </>
                                        }
									</Col>
								</CardBody>
							</Card>
						</Col>
                        <Col md="2" xs="2">
                            <Card style={{ borderStyle: 'solid', borderWidth: '1px', backgroundColor: '#f6f9fc', height: '99%' }}>
                                <CardBody style={{ padding: '12px' }}>
                                    <Col md="12" className="text-primary" >
                                        <div><label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px' }}>{ t('Auctions.Detail.Tiers.Tier_Name') + ':' }</label><label style={{ marginRight:'5px', marginBottom: '0px', fontSize: '12px', fontWeight: 'bolder' }}>{ auctionListItem.auctionTier.auctionTierName }</label></div>
                                        <div><label style={{ marginRight:'5px', fontSize: '12px' }}>CDLSI/{ auctionListItem.make }</label></div>
                                        <div><label style={{ marginRight:'5px', fontSize: '12px' }}>{ t('Auctions.Listing.Off_Lease') }</label></div>
                                        <Row style={{ marginTop: '25px' }}>
                                            <Col>
                                                { auctionListItem.auctionTier.isOnMemberWatchlist &&
                                                    <Button
														block
														color="secondary"
														size="sm"
														className="btn-block"
														style={{ marginTop:'5px' }}
														onClick={ () => removeFromWatchListClicked(auctionListItem) }
													>
														{ t('Auctions.Listing.Remove_From_Watchlist') }
													</Button>
                                                }
                                                { !auctionListItem.auctionTier.isOnMemberWatchlist &&
                                                    <Button
														block
														color="secondary"
														size="sm"
														className="btn-block"
														style={{ marginTop:'5px' }}
														onClick={ () => addToWatchlistClicked(auctionListItem) }
													>
														{ t('Auctions.Listing.Add_To_Watchlist') }
													</Button>
                                                }
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <Button
													block
													color="primary"
													size="sm"
													className="btn-block"
													style={{ marginTop:'5px' }}
													onClick={ () => viewDetailsClicked(auctionListItem) }
												>
													{ t('Auctions.Listing.View_Vehicle_Details') }
												</Button>
                                            </Col>
                                        </Row>
                                    </Col>
                                </CardBody>
                            </Card>
                        </Col>
					</Row>

					<Row className="row-example">
						<Col xs="6">
							{
								SiteService.isDealerBuyNowVisible(auctionListItem) &&
								<Button
									block
                                    color="primary"
                                    className="btn-block"
                                    style={{ display: 'inline-block', cursor: 'pointer' }}
                                    onClick={ () => placeBuyNowClicked(auctionListItem) }
                                    disabled={ ((!SiteService.isInRole(sessionContext.loginSession, Enumeration.UserRoles.AuctionBuyer) && !SiteService.isInRole(sessionContext.loginSession, Enumeration.UserRoles.AuctionHybrid)) || auctionListItem.secondsRemaining < 1) }
                                >
									{ t('Auctions.Listing.Buy_Now') }
								</Button>
							}
						</Col>
						<Col xs="6">
							{
								SiteService.isDealerPlaceBidVisible(auctionListItem) &&
								<Button
									block
                                    color="primary"
                                    className="btn-block"
                                    style={{ display: 'inline-block', marginTop: 0, cursor: 'pointer' }}
                                    onClick={ () => placeBidClicked(auctionListItem) }
                                    disabled={ ((!SiteService.isInRole(sessionContext.loginSession, Enumeration.UserRoles.AuctionBuyer) && !SiteService.isInRole(sessionContext.loginSession, Enumeration.UserRoles.AuctionHybrid)) || ((auctionListItem.secondsRemaining < 1) && !(auctionListItem.auctionTier && auctionListItem.auctionTier.yourBid && auctionListItem.auctionTier.yourBid.counterBid))) }
                                >
									{
										SiteService.bidButtonLabel(auctionListItem, t)
									}
								</Button>
							}
						</Col>
					</Row>
				</CardBody>
			</Card>
			
			<Modal
				size="lg"
				isOpen={selectedPicFilePath.length > 0}
				toggle={() => handleLargeInspectionPicClicked()}
			>
				<ModalHeader toggle={ () => handleLargeInspectionPicClicked() } charCode="Y"></ModalHeader>
				<ModalBody>
					<Card className="border-0 mb-0">
						<CardBody>
							<div style={{ textAlign: 'center' }}>
								<img className="inspection-pic-lg" src={selectedPicFilePath} width="700px" height="600px" alt="..."/>
							</div>
							<div style={{ textAlign: 'center' }}>
								<Button
									block
									color="primary"
									onClick={() => handleLargeInspectionPicClicked()}
									style={{ marginLeft: '10px', marginTop: '10px' }}
								>
									Close
								</Button>
							</div>
						</CardBody>
					</Card>
				</ModalBody>
			</Modal>
		</div>
	)
}

export default AuctionListGroupItem
